import { useRef, useState, type FormEvent } from "react";
import { useLocation, useRoute } from "wouter";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { EmptyState } from "../components/EmptyState";
import { Footer } from "../components/Footer";
import { Field } from "../components/Field";
import { Spinner } from "../components/Spinner";
import { ApiError, apiGet, apiSend } from "../lib/customFetch";
import { advanceAuthenticationGeneration } from "../lib/session-events";
import styles from "./AuthLayout.module.css";

interface ClaimResponse {
  navn: string;
  registreringsMode: "stempel" | "standard";
}

interface ClaimIdentity {
  navn: string;
  rolle: "ejer" | "administrator" | "bogholder" | "medarbejder";
  registreringsMode: "stempel" | "standard";
}

interface AdministrativeClaimResponse {
  navn: string;
  rolle: "administrator" | "bogholder";
}

type ClaimState =
  | { status: "idle"; flow: "choose" | "administrative" }
  | { status: "busy" }
  | {
      status: "done";
      kind: "employee";
      navn: string;
      registreringsMode: "stempel" | "standard";
    }
  | {
      status: "done";
      kind: "administrative";
      navn: string;
      rolle: "administrator" | "bogholder";
    }
  | { status: "failed"; besked: string; flow: "employee" | "administrative" };

function claimFejlbesked(
  err: unknown,
  flow: "employee" | "administrative",
): string {
  if (err instanceof ApiError && err.status === 410) {
    return "Invitationen er udløbet eller allerede brugt — bed din arbejdsgiver om en ny.";
  }
  if (err instanceof ApiError && err.status === 404) {
    if (flow === "administrative") {
      return "Linket giver ikke adgang til administration. Vælg medarbejderaktivering, eller bed ejeren om den rigtige invitation.";
    }
    return "Invitationen findes ikke. Kontrollér linket.";
  }
  return "Noget gik galt. Prøv igen.";
}

/**
 * Medarbejderens FØRSTE møde med produktet — åbnes fra QR/link på
 * telefonen. Ét tryk: den signerede cookie-session kobler browseren på
 * virksomheden. Ingen kode, intet password eller browserlagret råtoken.
 */
export function ClaimPage() {
  const [, params] = useRoute("/claim/:token");
  const [, navigate] = useLocation();
  const [state, setState] = useState<ClaimState>({ status: "idle", flow: "choose" });
  const [navn, setNavn] = useState("");
  const [password, setPassword] = useState("");
  const attemptIdRef = useRef<string | null>(null);

  const token = params?.token ?? "";

  const activate = async () => {
    attemptIdRef.current ??= crypto.randomUUID();
    setState({ status: "busy" });
    try {
      const res = await apiSend<ClaimResponse>(
        "POST",
        `/api/invites/${token}/claim`,
        { attemptId: attemptIdRef.current },
      );
      advanceAuthenticationGeneration();
      setState({
        status: "done",
        kind: "employee",
        navn: res.navn,
        registreringsMode: res.registreringsMode,
      });
    } catch (err: unknown) {
      // Claim er en engangsmutation: cookien kan være modtaget, selv om
      // en 2xx-responsebody afbrydes. En ApiError er derimod et autoritativt
      // HTTP-afslag og må aldrig skjules af en eksisterende anden cookie.
      if (!(err instanceof ApiError)) {
        try {
          const me = await apiGet<ClaimIdentity>("/api/me");
          if (me.rolle === "medarbejder") {
            advanceAuthenticationGeneration();
            setState({
              status: "done",
              kind: "employee",
              navn: me.navn,
              registreringsMode: me.registreringsMode,
            });
            return;
          }
        } catch {
          // Den oprindelige claimfejl giver den relevante besked nedenfor.
        }
      }
      setState({ status: "failed", besked: claimFejlbesked(err, "employee"), flow: "employee" });
    }
  };

  const activateAdministrative = async (event: FormEvent) => {
    event.preventDefault();
    setState({ status: "busy" });
    try {
      const res = await apiSend<AdministrativeClaimResponse>(
        "POST",
        `/api/invites/${token}/administrative-claim`,
        { navn: navn.trim(), password },
      );
      advanceAuthenticationGeneration();
      setState({ status: "done", kind: "administrative", ...res });
    } catch (err: unknown) {
      if (!(err instanceof ApiError)) {
        try {
          const me = await apiGet<ClaimIdentity>("/api/me");
          if (me.rolle === "administrator" || me.rolle === "bogholder") {
            advanceAuthenticationGeneration();
            setState({
              status: "done",
              kind: "administrative",
              navn: me.navn,
              rolle: me.rolle,
            });
            return;
          }
        } catch {
          // Den oprindelige claimfejl giver den relevante besked nedenfor.
        }
      }
      setState({ status: "failed", besked: claimFejlbesked(err, "administrative"), flow: "administrative" });
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.box}>
        <div className={styles.brand}>
          app<span>smeden</span>
        </div>
        <Card title="Kobl din telefon på">
          {state.status === "idle" && state.flow === "choose" && (
            <div className={styles.form}>
              <p>
                Du har modtaget en invitation til AppSmeden. Vælg den adgang,
                invitationen er oprettet til.
              </p>
              <Button
                aria-label="Aktivér denne telefon"
                onClick={() => void activate()}
              >
                Medarbejder · aktivér telefon
              </Button>
              <Button
                variant="secondary"
                onClick={() => setState({ status: "idle", flow: "administrative" })}
              >
                Opret adgang til administration
              </Button>
            </div>
          )}
          {state.status === "idle" && state.flow === "administrative" && (
            <form className={styles.form} onSubmit={(event) => void activateAdministrative(event)}>
              <p>
                Opret din personlige adgang. Din rolle og email er allerede
                bundet til invitationen og kan ikke ændres her.
              </p>
              <Field
                label="Dit navn"
                value={navn}
                onChange={(event) => setNavn(event.target.value)}
                autoComplete="name"
                autoFocus
                required
              />
              <Field
                label="Vælg adgangskode"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
                hint="Mindst 10 tegn. Brug en adgangskode, du ikke bruger andre steder."
              />
              <Button type="submit">Opret adgang</Button>
              <Button
                variant="ghost"
                onClick={() => setState({ status: "idle", flow: "choose" })}
              >
                Tilbage
              </Button>
            </form>
          )}
          {state.status === "busy" && <Spinner large />}
          {state.status === "done" && (
            <EmptyState
              illustration="✓"
              title={`Velkommen, ${state.navn}!`}
              description={
                state.kind === "administrative"
                  ? state.rolle === "bogholder"
                    ? "Din læseadgang er klar — du kan gennemgå rapporter og hente eksportfiler."
                    : "Din administrative adgang er klar."
                  : state.registreringsMode === "standard"
                  ? "Telefonen er koblet på — din faste standarduge er klar."
                  : "Telefonen er koblet på — du kan stemple ind med det samme."
              }
              cta={
                <Button
                  onClick={() =>
                    navigate(
                      state.kind === "administrative"
                        ? state.rolle === "bogholder"
                          ? "/clockin?fane=rapport"
                          : "/"
                        : "/stemple",
                    )
                  }
                >
                  {state.kind === "administrative"
                    ? state.rolle === "bogholder"
                      ? "Åbn rapporter"
                      : "Åbn administrationen"
                    : state.registreringsMode === "standard"
                    ? "Se din standarduge"
                    : "Åbn stemplingen"}
                </Button>
              }
            />
          )}
          {state.status === "failed" && (
            <EmptyState
              title="Kunne ikke aktivere"
              description={state.besked}
              cta={
                <div className={styles.form}>
                  <Button onClick={() =>
                    state.flow === "employee"
                      ? void activate()
                      : setState({ status: "idle", flow: "administrative" })
                  }>
                    Prøv igen
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setState({ status: "idle", flow: "choose" })}
                  >
                    Tilbage til valg
                  </Button>
                </div>
              }
            />
          )}
        </Card>
        <Footer />
      </div>
    </div>
  );
}
